import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone } from 'lucide-react';

export const HeroVideo = ({ videoSrc, title, subtitle, primaryCTA, secondaryCTA }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="relative min-h-[72vh] md:min-h-[84vh] w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video Background */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        playsInline
        muted
        loop
        autoPlay
        preload="metadata"
        data-testid="hero-bg-video"
      >
        <source src={videoSrc} type="video/mp4" />
      </video>

      {/* Overlay */}
      <div
        className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_70%_10%,rgba(0,0,0,0.15)_0%,rgba(0,0,0,0.35)_70%,rgba(0,0,0,0.45)_100%)]"
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative h-full min-h-[72vh] md:min-h-[84vh] flex items-end md:items-center">
        <div className="container-custom pb-12 md:pb-0 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="max-w-2xl"
          >
            <h1
              className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-4"
              data-testid="hero-heading"
            >
              {title}
            </h1>
            <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              {subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to={primaryCTA.link} data-testid="primary-cta-button">
                <Button className="btn-primary h-12 px-8 text-base" size="lg">
                  {primaryCTA.text}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to={secondaryCTA.link} data-testid="secondary-cta-button">
                <Button
                  variant="outline"
                  className="h-12 px-8 text-base border-2 border-white/40 text-white hover:bg-white/10 hover:text-white"
                  size="lg"
                >
                  {secondaryCTA.text}
                  <Phone className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Noise Overlay */}
      <div className="noise-overlay" />
    </section>
  );
};
